<script>
/**
 * 감정평가 상세 페이지.
 *
 * 구성: 예약 정보, 감정 로트, 지점은 품목별 감정+본사 검품을 한 표(7열)·검품 열은 조회.
 * 교환완료(ACTIVE 로트) 이후 지점 감정 입력은 조회만 가능.
 */
import { getAppraisalDetail, completeBranchAppraisal, completeHqAppraisal } from '@/api/appraisal.js'
import { getAppraisalItemList } from '@/api/appraisalItem.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'AppraisalDetailPage',

  data() {
    return {
      /** 감정예약코드 (URL 파라미터에서 취득) */
      appraisalBookingCode: this.$route.params.code,

      /** 현재 로그인 관리자 정보 */
      adminInfo: getAdminInfo(),

      /* ── 상세 데이터 ── */
      booking: null,

      /* ── UI 상태 ── */
      loading: false,

      /**
       * 상태 chip 색상 맵 (서버 statusLabel 기준, 사용자 앱과 동일).
       * 예약대기=info, 진행중=warning, 감정완료/교환완료=success, 취소=error
       */
      statusLabelColorMap: {
        예약대기: 'info',
        진행중: 'warning',
        감정완료: 'success',
        교환완료: 'success',
        취소: 'error',
      },

      /** 품목별 감정: 금/은 탭 */
      metalTab: 'GOLD',
      itemsBySpot: { GOLD: [], SILVER: [] },
      /** appraisal_item_code → 측정 실중량(g) 문자열/숫자 */
      measuredByCode: {},
      loadingAppraisalItems: false,
      appraisalItemsLoadError: '',
      /** 마스터 기준비율 없음(기타) 품목: 기준비율 칸 입력값(100% 기준, 예: 15 → 15%) */
      extraPercentByCode: {},
      savingItemResults: false,
      /** 감정 완료 성공 후 안내 모달 */
      completeSuccessDialog: false,

      /** 본사 검품 입력 (step=9) */
      hqMetalTab: 'GOLD',
      hqMeasuredByCode: {},
      hqExtraPercentByCode: {},
      savingHqResults: false,
      hqCompleteSuccessDialog: false,
    }
  },

  computed: {
    /**
     * 현재 관리자가 HQ(본사) 관리자인지 여부.
     * HQ는 감정 작성 불가.
     */
    isHq() {
      return this.adminInfo?.adminType === 'HQ'
    },

    /**
     * 지점 품목별 감정 카드 표시(취소 제외). 본사는 미표시.
     */
    showBranchItemCard() {
      if (this.isHq || !this.booking) return false
      const st = (this.booking.status || '').toUpperCase()
      return st !== 'CANCEL'
    },

    /** 교환완료(COMPLETE + ACTIVE 로트): 조회만 */
    itemAppraisalReadOnly() {
      if (!this.booking) return true
      const st = (this.booking.status || '').toUpperCase()
      if (st !== 'COMPLETE') return false
      return this.booking.active === true
    },

    /** 금/은 입력·감정 완료 버튼 활성 */
    canEditBranchAppraisal() {
      return this.showBranchItemCard && !this.itemAppraisalReadOnly
    },

    /**
     * HQ: 지점 품목(step=1)·검품(step=9) 통합 테이블 표시.
     * 예약이 COMPLETE가 아니어도(진행중 ING 등) 지점 측정이 있으면 같은 화면에서 검품 열까지 보여 준다.
     * 교환완료(ACTIVE 로트)여도 status가 COMPLETE이면 본사 검품 카드를 숨기지 않는다(hq_yn 미완료 검품 가능).
     * 검품 입력·검품 완료 버튼은 {@link #canEditHqInspection} 이 true일 때만 활성화.
     */
    showHqUnifiedAppraisalCard() {
      if (!this.isHq || !this.booking) return false
      const st = (this.booking.status || '').toUpperCase()
      if (st === 'CANCEL') return false
      const hasBranch = (this.booking.appraisalItemResults || []).length > 0
      const hasHq = (this.booking.hqAppraisalItemResults || []).length > 0
      if (hasBranch || hasHq) return true
      return st === 'COMPLETE' || st === 'ING'
    },

    hqInspectionDone() {
      return (this.booking?.hqYn || '').toUpperCase() === 'Y'
    },

    /** 검품중량 입력·검품 완료: 감정완료(COMPLETE)이고 hq_yn 미완료면 교환 여부와 무관 */
    canEditHqInspection() {
      if (!this.showHqUnifiedAppraisalCard || !this.booking) return false
      if (this.hqInspectionDone) return false
      const st = (this.booking.status || '').toUpperCase()
      return st === 'COMPLETE'
    },

    currentHqAppraisalItems() {
      return this.itemsBySpot[this.hqMetalTab] || []
    },

    hqTotalMeasuredWeightG() {
      let sum = 0
      for (const row of this.currentHqAppraisalItems) {
        const w = Number(this.hqMeasuredByCode[row.appraisalItemCode])
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    hqTotalConvertedWeightG() {
      let sum = 0
      for (const row of this.currentHqAppraisalItems) {
        const w = Number(this.hqMeasuredByCode[row.appraisalItemCode])
        if (!Number.isFinite(w) || w <= 0) continue
        sum += w * this.effectiveRatioForRowHq(row)
      }
      return sum
    },

    /** 현재 탭 품목 중 지점(step=1) 측정 실중량 합 */
    hqTabBranchTotalMeasuredG() {
      let sum = 0
      for (const row of this.currentHqAppraisalItems) {
        const br = this.branchResultRow(row.appraisalItemCode)
        const w = br?.checkWeight != null ? Number(br.checkWeight) : NaN
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    /** 현재 탭 품목 중 지점(step=1) 환산 중량 합 */
    hqTabBranchTotalConvertedG() {
      let sum = 0
      for (const row of this.currentHqAppraisalItems) {
        const br = this.branchResultRow(row.appraisalItemCode)
        const w = br?.conversionWeight != null ? Number(br.conversionWeight) : NaN
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    /** 지점 품목 탭(금/은) 기준 검품 측정 합 */
    branchTabHqMeasuredTotalG() {
      let sum = 0
      for (const row of this.currentAppraisalItems) {
        const hr = this.hqResultRow(row.appraisalItemCode)
        const w = hr?.checkWeight != null ? Number(hr.checkWeight) : NaN
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    /** 지점 품목 탭 기준 검품 환산 합 */
    branchTabHqConvertedTotalG() {
      let sum = 0
      for (const row of this.currentAppraisalItems) {
        const hr = this.hqResultRow(row.appraisalItemCode)
        const w = hr?.conversionWeight != null ? Number(hr.conversionWeight) : NaN
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    /**
     * 기 작성된 감정 로트가 있는지 여부.
     */
    hasLots() {
      return this.booking?.lots?.length > 0
    },

    /** 현재 탭의 appraisal_item 목록 */
    currentAppraisalItems() {
      return this.itemsBySpot[this.metalTab] || []
    },

    /** 총 측정 실중량(g) — 현재 탭 */
    totalMeasuredWeightG() {
      let sum = 0
      for (const row of this.currentAppraisalItems) {
        const code = row.appraisalItemCode
        const w = Number(this.measuredByCode[code])
        if (Number.isFinite(w) && w > 0) sum += w
      }
      return sum
    },

    /** 총 환산 중량(g) — 측정 × 적용비율(마스터 환산비율 또는 기타 입력%, 레거시는 기준비율) */
    totalConvertedWeightG() {
      let sum = 0
      for (const row of this.currentAppraisalItems) {
        const code = row.appraisalItemCode
        const w = Number(this.measuredByCode[code])
        if (!Number.isFinite(w) || w <= 0) continue
        sum += w * this.effectiveRatioForRow(row)
      }
      return sum
    },
  },

  mounted() {
    this.fetchDetail()
  },

  methods: {
    /**
     * 감정예약 상세 정보를 서버에서 조회한다.
     */
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getAppraisalDetail(this.appraisalBookingCode)
        if (res.data?.code === 0) {
          this.booking = res.data.data
          this.$nextTick(() => {
            if (this.showBranchItemCard) this.loadAppraisalMasterItems()
            else if (
              this.isHq &&
              this.booking &&
              (this.booking.status || '').toUpperCase() !== 'CANCEL'
            ) {
              this.loadAppraisalMasterItems()
            }
          })
        } else {
          this.$router.push('/appraisal')
        }
      } catch (e) {
        console.error('[AppraisalDetailPage] fetchDetail error:', e)
        this.$router.push('/appraisal')
      } finally {
        this.loading = false
      }
    },

    /**
     * 지점 감정 화면용 appraisal_item 마스터 (금·은) 로드.
     */
    async loadAppraisalMasterItems() {
      this.loadingAppraisalItems = true
      this.appraisalItemsLoadError = ''
      try {
        const [gRes, sRes] = await Promise.all([
          getAppraisalItemList('GOLD'),
          getAppraisalItemList('SILVER'),
        ])
        if (gRes.data?.code === 0) {
          this.itemsBySpot.GOLD = Array.isArray(gRes.data.data) ? gRes.data.data : []
        } else {
          this.itemsBySpot.GOLD = []
          this.appraisalItemsLoadError = gRes.data?.message || '금 품목 목록을 불러오지 못했습니다.'
        }
        if (sRes.data?.code === 0) {
          this.itemsBySpot.SILVER = Array.isArray(sRes.data.data) ? sRes.data.data : []
        } else {
          this.itemsBySpot.SILVER = []
          if (!this.appraisalItemsLoadError) {
            this.appraisalItemsLoadError = sRes.data?.message || '은 품목 목록을 불러오지 못했습니다.'
          }
        }
      } catch (e) {
        console.error('[AppraisalDetailPage] loadAppraisalMasterItems', e)
        this.appraisalItemsLoadError = '품목 목록을 불러오는 중 오류가 발생했습니다.'
      } finally {
        this.loadingAppraisalItems = false
        if (this.booking) {
          if (this.isHq) this.mergeHqAppraisalInputsFromBooking()
          else this.mergeAppraisalItemResultsFromBooking()
        }
      }
    },

    /**
     * 서버에 저장된 지점 품목별 결과(step=1)를 입력칸에 반영.
     */
    mergeAppraisalItemResultsFromBooking() {
      const rows = this.booking?.appraisalItemResults
      const goldList = this.itemsBySpot.GOLD || []
      const silverList = this.itemsBySpot.SILVER || []
      const goldCodes = new Set(goldList.map(r => r.appraisalItemCode))
      const silverCodes = new Set(silverList.map(r => r.appraisalItemCode))
      const allMaster = [...goldList, ...silverList]
      const next = { ...this.measuredByCode }
      const nextP = { ...this.extraPercentByCode }
      for (const c of goldCodes) {
        delete next[c]
        delete nextP[c]
      }
      for (const c of silverCodes) {
        delete next[c]
        delete nextP[c]
      }
      for (const r of rows || []) {
        if (!goldCodes.has(r.appraisalItemCode) && !silverCodes.has(r.appraisalItemCode)) continue
        if (r.checkWeight != null) {
          next[r.appraisalItemCode] = String(r.checkWeight)
        }
        const meta = allMaster.find(i => i.appraisalItemCode === r.appraisalItemCode)
        if (meta && (meta.standardPercent == null || meta.standardPercent === '') && r.checkPercent != null) {
          nextP[r.appraisalItemCode] = this.ratioToPercentInputString(r.checkPercent)
        }
      }
      this.measuredByCode = next
      this.extraPercentByCode = nextP
    },

    /**
     * 본사 검품 입력란: 저장된 검품(step=9) 우선, 없으면 지점(step=1) 값으로 초기화.
     */
    mergeHqAppraisalInputsFromBooking() {
      const preferHq =
        this.booking?.hqAppraisalItemResults &&
        this.booking.hqAppraisalItemResults.length > 0
      const rows = preferHq
        ? this.booking.hqAppraisalItemResults
        : this.booking?.appraisalItemResults
      const goldList = this.itemsBySpot.GOLD || []
      const silverList = this.itemsBySpot.SILVER || []
      const goldCodes = new Set(goldList.map((r) => r.appraisalItemCode))
      const silverCodes = new Set(silverList.map((r) => r.appraisalItemCode))
      const allMaster = [...goldList, ...silverList]
      const next = {}
      const nextP = {}
      for (const r of rows || []) {
        if (!goldCodes.has(r.appraisalItemCode) && !silverCodes.has(r.appraisalItemCode)) continue
        if (r.checkWeight != null) {
          next[r.appraisalItemCode] = String(r.checkWeight)
        }
        const meta = allMaster.find((i) => i.appraisalItemCode === r.appraisalItemCode)
        if (meta && (meta.standardPercent == null || meta.standardPercent === '') && r.checkPercent != null) {
          nextP[r.appraisalItemCode] = this.ratioToPercentInputString(r.checkPercent)
        }
      }
      this.hqMeasuredByCode = next
      this.hqExtraPercentByCode = nextP
    },

    spotLabelForAppraisalItemCode(code) {
      if ((this.itemsBySpot.GOLD || []).some((x) => x.appraisalItemCode === code)) return '금'
      if ((this.itemsBySpot.SILVER || []).some((x) => x.appraisalItemCode === code)) return '은'
      return '-'
    },

    /** 지점 품목별 감정(step=1) 해당 품목 행 */
    branchResultRow(appraisalItemCode) {
      const rows = this.booking?.appraisalItemResults || []
      return rows.find((r) => r.appraisalItemCode === appraisalItemCode)
    },

    /** 본사 검품(step=9) 해당 품목 행 */
    hqResultRow(appraisalItemCode) {
      const rows = this.booking?.hqAppraisalItemResults || []
      return rows.find((r) => r.appraisalItemCode === appraisalItemCode)
    },

    /** 지점 측정에 쓰인 적용비율(%) 표시 — 저장값 우선, 없으면 마스터 기준비율 */
    branchAppliedRatioLabel(row) {
      const br = this.branchResultRow(row.appraisalItemCode)
      if (br?.checkPercent != null) {
        return `${this.ratioToPercentInputString(br.checkPercent)}%`
      }
      if (row.standardPercent != null && row.standardPercent !== '') {
        return this.formatStandardRatioLabel(row.standardPercent)
      }
      return '-'
    },

    /** 기타 품목: 검품 입력 비활성(진행중 등)일 때 적용비율 표시 */
    hqAppliedRatioReadonlyText(row) {
      const r = this.effectiveRatioForRowHq(row)
      if (Number.isFinite(r) && r > 0) {
        return `${this.ratioToPercentInputString(r)}%`
      }
      const br = this.branchResultRow(row.appraisalItemCode)
      if (br?.checkPercent != null) {
        return `${this.ratioToPercentInputString(br.checkPercent)}%`
      }
      return '-'
    },

    /** 현재 탭(금/은) 한글 — 테이블 현물 열 */
    hqSpotLabelForTab() {
      return this.hqMetalTab === 'GOLD' ? '금' : this.hqMetalTab === 'SILVER' ? '은' : '-'
    },

    /** 지점 품목별 감정 탭 — 통합 표 현물 열 */
    branchSpotLabelForTab() {
      return this.metalTab === 'GOLD' ? '금' : this.metalTab === 'SILVER' ? '은' : '-'
    },

    appraisalItemDisplayName(code) {
      const all = [...(this.itemsBySpot.GOLD || []), ...(this.itemsBySpot.SILVER || [])]
      const m = all.find((i) => i.appraisalItemCode === code)
      return m?.appraisalItemName || code
    },

    effectiveRatioForRowHq(row) {
      if (row.conversionPercent != null && row.conversionPercent !== '') {
        const c = Number(row.conversionPercent)
        if (Number.isFinite(c)) return c
      }
      if (row.standardPercent == null || row.standardPercent === '') {
        const pct = Number(this.hqExtraPercentByCode[row.appraisalItemCode])
        return Number.isFinite(pct) ? pct / 100 : 0
      }
      const n = Number(row.standardPercent)
      return Number.isFinite(n) ? n : 0
    },

    rowConvertedWeightHq(row) {
      const w = Number(this.hqMeasuredByCode[row.appraisalItemCode])
      if (!Number.isFinite(w) || w <= 0) return null
      const r = this.effectiveRatioForRowHq(row)
      return w * r
    },

    onHqMeasuredInput(code, v) {
      if (v === '' || v == null) {
        const next = { ...this.hqMeasuredByCode }
        delete next[code]
        this.hqMeasuredByCode = next
        return
      }
      this.hqMeasuredByCode = { ...this.hqMeasuredByCode, [code]: v }
    },

    onHqExtraPercentInput(code, v) {
      if (v === '' || v == null) {
        const next = { ...this.hqExtraPercentByCode }
        delete next[code]
        this.hqExtraPercentByCode = next
        return
      }
      this.hqExtraPercentByCode = { ...this.hqExtraPercentByCode, [code]: v }
    },

    buildLinesForHqSpot(spotKey) {
      const rows = this.itemsBySpot[spotKey] || []
      const lines = []
      const metalLabel = spotKey === 'GOLD' ? '금' : '은'
      for (const row of rows) {
        const w = Number(this.hqMeasuredByCode[row.appraisalItemCode])
        if (!Number.isFinite(w) || w <= 0) continue
        const line = { appraisalItemCode: row.appraisalItemCode, checkWeight: w }
        const hasMasterRatio = row.standardPercent != null && row.standardPercent !== ''
        if (!hasMasterRatio) {
          const pct = Number(this.hqExtraPercentByCode[row.appraisalItemCode])
          if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
            window.alert(
              `${metalLabel} 품목 "${row.appraisalItemName || row.appraisalItemCode}"은(는) 기준비율 칸에 0~100 사이 비율(%)을 입력해 주세요.`,
            )
            return null
          }
          line.checkPercent = pct / 100
        }
        lines.push(line)
      }
      return lines
    },

    async submitHqAppraisalComplete() {
      if (!this.canEditHqInspection) return
      const goldLines = this.buildLinesForHqSpot('GOLD')
      if (goldLines === null) return
      const silverLines = this.buildLinesForHqSpot('SILVER')
      if (silverLines === null) return
      if (goldLines.length === 0 && silverLines.length === 0) {
        window.alert(
          '금 또는 은 품목 중 측정 실중량이 입력된 행이 최소 1건 이상 필요합니다.',
        )
        return
      }

      this.savingHqResults = true
      try {
        const res = await completeHqAppraisal(this.appraisalBookingCode, {
          goldLines,
          silverLines,
        })
        if (res.data?.code === 0) {
          this.booking = res.data.data
          this.mergeHqAppraisalInputsFromBooking()
          this.hqCompleteSuccessDialog = true
        } else {
          window.alert(res.data?.message || '저장에 실패했습니다.')
        }
      } catch (e) {
        console.error('[AppraisalDetailPage] submitHqAppraisalComplete', e)
        const msg = e?.response?.data?.message || '저장 중 오류가 발생했습니다.'
        window.alert(msg)
      } finally {
        this.savingHqResults = false
      }
    },

    onCloseHqCompleteDialog() {
      this.hqCompleteSuccessDialog = false
    },

    /**
     * 환산 중량용 적용 비율(0~1).
     * 마스터 conversion_percent 우선, 기타 품목은 입력 %(100기준)÷100, 없으면 기준비율(레거시).
     */
    effectiveRatioForRow(row) {
      if (row.conversionPercent != null && row.conversionPercent !== '') {
        const c = Number(row.conversionPercent)
        if (Number.isFinite(c)) return c
      }
      if (row.standardPercent == null || row.standardPercent === '') {
        const pct = Number(this.extraPercentByCode[row.appraisalItemCode])
        return Number.isFinite(pct) ? pct / 100 : 0
      }
      const n = Number(row.standardPercent)
      return Number.isFinite(n) ? n : 0
    },

    /** 서버 checkPercent(0~1) → 입력칸용 문자열(15, 33.5 등) */
    ratioToPercentInputString(ratio) {
      const r = Number(ratio)
      if (!Number.isFinite(r)) return ''
      const pct = r * 100
      return String(pct.toFixed(6).replace(/\.?0+$/, ''))
    },

    /** 행별 환산 중량(g) */
    rowConvertedWeight(row) {
      const w = Number(this.measuredByCode[row.appraisalItemCode])
      if (!Number.isFinite(w) || w <= 0) return null
      const r = this.effectiveRatioForRow(row)
      return w * r
    },

    /** DB 기준비율(0~1) → 표시용 % */
    formatStandardRatioLabel(standardPercent) {
      if (standardPercent == null || standardPercent === '') return '-'
      const n = Number(standardPercent)
      if (!Number.isFinite(n)) return '-'
      const pct = n * 100
      const s = pct % 1 === 0 ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, '')
      return `${s}%`
    },

    formatWeightG(val) {
      if (val == null || val === '') return '-'
      const n = Number(val)
      if (!Number.isFinite(n)) return '-'
      const t = n.toFixed(3).replace(/\.?0+$/, '')
      return `${t} g`
    },

    onMeasuredInput(code, v) {
      if (v === '' || v == null) {
        const next = { ...this.measuredByCode }
        delete next[code]
        this.measuredByCode = next
        return
      }
      this.measuredByCode = { ...this.measuredByCode, [code]: v }
    },

    onExtraPercentInput(code, v) {
      if (v === '' || v == null) {
        const next = { ...this.extraPercentByCode }
        delete next[code]
        this.extraPercentByCode = next
        return
      }
      this.extraPercentByCode = { ...this.extraPercentByCode, [code]: v }
    },

    /**
     * 금 또는 은 마스터 행 → API용 lines (검증 실패 시 null).
     * @param {'GOLD'|'SILVER'} spotKey
     */
    buildLinesForSpot(spotKey) {
      const rows = this.itemsBySpot[spotKey] || []
      const lines = []
      const metalLabel = spotKey === 'GOLD' ? '금' : '은'
      for (const row of rows) {
        const w = Number(this.measuredByCode[row.appraisalItemCode])
        if (!Number.isFinite(w) || w <= 0) continue
        const line = { appraisalItemCode: row.appraisalItemCode, checkWeight: w }
        const hasMasterRatio = row.standardPercent != null && row.standardPercent !== ''
        if (!hasMasterRatio) {
          const pct = Number(this.extraPercentByCode[row.appraisalItemCode])
          if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
            window.alert(
              `${metalLabel} 품목 "${row.appraisalItemName || row.appraisalItemCode}"은(는) 기준비율 칸에 0~100 사이 비율(%)을 입력해 주세요.`,
            )
            return null
          }
          line.checkPercent = pct / 100
        }
        lines.push(line)
      }
      return lines
    },

    /** 금·은 중 하나 이상 입력 후 감정 완료 처리 */
    async submitAppraisalComplete() {
      if (!this.canEditBranchAppraisal) return
      const goldLines = this.buildLinesForSpot('GOLD')
      if (goldLines === null) return
      const silverLines = this.buildLinesForSpot('SILVER')
      if (silverLines === null) return
      if (goldLines.length === 0 && silverLines.length === 0) {
        window.alert(
          '금 또는 은 품목 중 측정 실중량이 입력된 행이 최소 1건 이상 필요합니다.',
        )
        return
      }

      this.savingItemResults = true
      try {
        const res = await completeBranchAppraisal(this.appraisalBookingCode, {
          goldLines,
          silverLines,
        })
        if (res.data?.code === 0) {
          this.booking = res.data.data
          this.mergeAppraisalItemResultsFromBooking()
          this.completeSuccessDialog = true
        } else {
          window.alert(res.data?.message || '저장에 실패했습니다.')
        }
      } catch (e) {
        console.error('[AppraisalDetailPage] submitAppraisalComplete', e)
        const msg = e?.response?.data?.message || '저장 중 오류가 발생했습니다.'
        window.alert(msg)
      } finally {
        this.savingItemResults = false
      }
    },

    onCloseAppraisalCompleteDialog() {
      this.completeSuccessDialog = false
      this.$router.push('/appraisal')
    },

    /** 상태 chip 색상 (서버에서 내려준 statusLabel 기준) */
    getStatusColor(statusLabel) {
      return this.statusLabelColorMap[statusLabel] || 'default'
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd HH:mm' 형식으로 포맷한다.
     */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd' 형식으로 포맷한다.
     */
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },

    /**
     * 숫자를 천 단위 콤마 포맷으로 반환한다.
     * null이면 '-' 반환.
     */
    formatPrice(val) {
      if (val == null) return '-'
      return Number(val).toLocaleString('ko-KR') + '원'
    },

    /** 현물구분 한국어 표시 */
    getSpotTypeLabel(spotType) {
      return spotType === 'GOLD' ? '금 (GOLD)' : spotType === 'SILVER' ? '은 (SILVER)' : spotType || '-'
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="mb-5">
      <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
        <span class="breadcrumb-link text-primary" @click="$router.push('/appraisal')">감정평가</span>
        <v-icon size="12">mdi-chevron-right</v-icon>
        <span>상세</span>
      </div>
      <div class="text-h5 font-weight-bold">감정평가 상세</div>
      <div class="text-body-2 text-grey mt-1">예약 정보 조회 및 품목별 감정</div>
    </div>

    <!-- 로딩 -->
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />

    <template v-if="!loading && booking">

      <!-- ── 예약 정보 (예약자명·연락처·이메일) ── -->
      <v-card rounded="xl" elevation="0" class="info-card mt-5 mb-4">
        <div class="info-card-header d-flex align-center justify-space-between px-5 py-4">
          <div class="d-flex align-center ga-2">
            <div class="header-icon-wrap bg-primary-lighten">
              <v-icon color="primary" size="18">mdi-calendar-check-outline</v-icon>
            </div>
            <span class="text-subtitle-2 font-weight-bold">예약 정보</span>
          </div>
          <v-chip
            :color="getStatusColor(booking.statusLabel)"
            size="small"
            label
            variant="flat"
            class="font-weight-bold"
          >
            {{ booking.statusLabel || booking.status || '-' }}
          </v-chip>
        </div>
        <v-divider />
        <div class="pa-5">
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">예약번호</div>
              <div class="info-value font-weight-bold text-body-2">
                {{ booking.appraisalBookingNo || booking.appraisalBookingCode }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">지점</div>
              <div class="info-value text-body-2">{{ booking.branchName || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">예약일</div>
              <div class="info-value text-body-2">{{ formatDate(booking.bookingDate) }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">예약자명</div>
              <div class="info-value font-weight-medium text-body-2">{{ booking.userName || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">연락처</div>
              <div class="info-value text-body-2">{{ booking.userPhone || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">이메일</div>
              <div class="info-value text-body-2">{{ booking.userEmail || '-' }}</div>
            </div>
            <div class="info-item">
              <div class="info-label">등록일시</div>
              <div class="info-value text-body-2 text-medium-emphasis">{{ formatDateTime(booking.insertDt) }}</div>
            </div>
            <div v-if="booking.cancelDt" class="info-item">
              <div class="info-label">취소일시</div>
              <div class="info-value text-body-2 text-error">{{ formatDateTime(booking.cancelDt) }}</div>
            </div>
            <div
              v-if="(booking.status || '').toUpperCase() !== 'CANCEL'"
              class="info-item"
            >
              <div class="info-label">본사 검품</div>
              <div class="info-value text-body-2 d-flex align-center ga-2">
                <v-chip
                  v-if="hqInspectionDone"
                  size="x-small"
                  color="success"
                  label
                  variant="tonal"
                >
                  완료
                </v-chip>
                <span v-else class="text-medium-emphasis">미완료</span>
              </div>
            </div>
          </div>
        </div>
      </v-card>

      <!-- ── 감정 내역 ── -->
      <v-card v-if="hasLots" rounded="xl" elevation="0" class="info-card mb-4">
        <div class="info-card-header d-flex align-center justify-space-between px-5 py-4">
          <div class="d-flex align-center ga-2">
            <div class="header-icon-wrap bg-success-lighten">
              <v-icon color="success" size="18">mdi-gold</v-icon>
            </div>
            <span class="text-subtitle-2 font-weight-bold">감정 내역</span>
          </div>
          <v-chip size="small" color="success" label variant="tonal" class="font-weight-bold">
            {{ booking.lots.length }}건
          </v-chip>
        </div>
        <v-divider />
        <div class="px-2 pb-2">
          <v-table density="comfortable" class="lot-table">
            <thead>
              <tr>
                <th class="lot-th">로트코드</th>
                <th class="lot-th">현물구분</th>
                <th class="lot-th">순도</th>
                <th class="lot-th">감정중량(g)</th>
                <th class="lot-th">완료중량(g)</th>
                <th class="lot-th">통화</th>
                <th class="lot-th">감정가</th>
                <th class="lot-th">등록일</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lot in booking.lots" :key="lot.userSpotLotCode" class="lot-row">
                <td class="text-caption text-medium-emphasis">{{ lot.userSpotLotCode }}</td>
                <td>
                  <v-chip
                    :color="lot.spotType === 'GOLD' ? 'warning' : 'blue-grey'"
                    size="x-small"
                    label
                    variant="tonal"
                    class="font-weight-medium"
                  >
                    {{ getSpotTypeLabel(lot.spotType) }}
                  </v-chip>
                </td>
                <td class="text-body-2">{{ lot.appraisalPurity }}</td>
                <td class="text-body-2">{{ lot.appraisalWeight }}</td>
                <td class="text-body-2">{{ lot.completeWeight ?? '-' }}</td>
                <td class="text-body-2">{{ lot.currency }}</td>
                <td class="text-body-2 font-weight-medium">{{ formatPrice(lot.appraisalPrice) }}</td>
                <td class="text-caption text-medium-emphasis">{{ formatDateTime(lot.insertDt) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card>

      <!-- ── HQ: 지점 입력 + 검품 입력 통합 (step=1 조회 + step=9 입력) ── -->
      <v-card v-if="showHqUnifiedAppraisalCard" rounded="xl" elevation="0" class="info-card mb-4">
        <div class="info-card-header d-flex align-center justify-space-between flex-wrap ga-2 px-5 py-4">
          <div class="d-flex align-center ga-2">
            <div class="header-icon-wrap bg-primary-lighten">
              <v-icon color="primary" size="18">mdi-clipboard-check-outline</v-icon>
            </div>
            <span class="text-subtitle-2 font-weight-bold">본사 검품</span>
            <v-chip v-if="hqInspectionDone" size="x-small" color="success" label variant="tonal">
              검품완료
            </v-chip>
          </div>
          <v-btn
            v-if="canEditHqInspection"
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="savingHqResults"
            :disabled="loadingAppraisalItems"
            prepend-icon="mdi-check"
            @click="submitHqAppraisalComplete"
          >
            검품 완료
          </v-btn>
        </div>
        <v-divider />
        <v-tabs v-model="hqMetalTab" color="primary" class="px-2">
          <v-tab value="GOLD" class="font-weight-medium">금 (GOLD)</v-tab>
          <v-tab value="SILVER" class="font-weight-medium">은 (SILVER)</v-tab>
        </v-tabs>
        <v-divider />
        <v-card-text class="pa-5 pt-4">
          <v-alert
            v-if="hqInspectionDone"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
            icon="mdi-information-outline"
          >
            본사 검품이 완료된 예약입니다. 내용은 조회만 가능합니다.
          </v-alert>
          <v-alert
            v-else-if="(booking.status || '').toUpperCase() !== 'COMPLETE'"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
            icon="mdi-information-outline"
          >
            예약이 감정완료(COMPLETE) 상태가 되면 검품 중량을 입력하고 「검품 완료」를 누를 수 있습니다. 지금은 지점 입력값만 확인할 수 있습니다.
          </v-alert>
          <v-alert
            v-if="appraisalItemsLoadError"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
          >
            {{ appraisalItemsLoadError }}
          </v-alert>
          <v-progress-linear
            v-if="loadingAppraisalItems"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div class="d-flex flex-wrap ga-4 mb-4 px-1 total-weight-summary">
            <div class="text-body-2">
              <span class="text-medium-emphasis">{{ hqMetalTab === 'GOLD' ? '금' : '은' }} · 지점 총 측정 실중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(hqTabBranchTotalMeasuredG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">지점 총 환산 중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(hqTabBranchTotalConvertedG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 중량</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(hqTotalMeasuredWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 환산</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(hqTotalConvertedWeightG) }}</span>
            </div>
          </div>

          <div class="hq-inspection-table-wrap">
            <v-table density="compact" class="appraisal-item-table border rounded-lg hq-inspection-table">
              <thead>
                <tr>
                  <th class="text-left text-no-wrap">현물</th>
                  <th class="text-left">품목</th>
                  <th class="text-end" style="min-width: 100px;">표시비율</th>
                  <th class="text-end text-no-wrap">계산비율</th>
                  <th class="text-end text-no-wrap">측정 실중량(g)</th>
                  <th class="text-end text-no-wrap">환산 중량(g)</th>
                  <th class="text-end" style="min-width: 132px;">검품중량(g)</th>
                  <th class="text-end text-no-wrap">검품 환산중량(g)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in currentHqAppraisalItems" :key="row.appraisalItemCode">
                  <td class="text-caption text-medium-emphasis text-no-wrap">{{ hqSpotLabelForTab() }}</td>
                  <td class="text-body-2">{{ row.appraisalItemName || '-' }}</td>
                  <td class="text-end py-2">
                    <template v-if="row.standardPercent != null && row.standardPercent !== ''">
                      <span class="text-caption">{{ branchAppliedRatioLabel(row) }}</span>
                    </template>
                    <template v-else-if="!canEditHqInspection">
                      <span class="text-caption text-medium-emphasis">{{ hqAppliedRatioReadonlyText(row) }}</span>
                    </template>
                    <v-text-field
                      v-else
                      :model-value="hqExtraPercentByCode[row.appraisalItemCode]"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      suffix="%"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="예: 30"
                      class="measure-input measure-input--ratio"
                      @update:model-value="v => onHqExtraPercentInput(row.appraisalItemCode, v)"
                    />
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatStandardRatioLabel(row.conversionPercent) }}
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatWeightG(branchResultRow(row.appraisalItemCode)?.checkWeight) }}
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatWeightG(branchResultRow(row.appraisalItemCode)?.conversionWeight) }}
                  </td>
                  <td class="text-end py-2">
                    <v-text-field
                      :model-value="hqMeasuredByCode[row.appraisalItemCode]"
                      type="number"
                      step="0.001"
                      min="0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="measure-input measure-input--hq-weight"
                      :disabled="!canEditHqInspection"
                      @update:model-value="v => onHqMeasuredInput(row.appraisalItemCode, v)"
                    />
                  </td>
                  <td class="text-end text-body-2 text-primary">
                    {{ formatWeightG(rowConvertedWeightHq(row)) }}
                  </td>
                </tr>
                <tr v-if="!loadingAppraisalItems && currentHqAppraisalItems.length === 0">
                  <td colspan="8" class="text-center text-medium-emphasis py-8">
                    등록된 품목이 없습니다.
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <div class="d-flex flex-wrap ga-4 mt-4 px-1 total-weight-summary">
            <div class="text-body-2">
              <span class="text-medium-emphasis">{{ hqMetalTab === 'GOLD' ? '금' : '은' }} · 지점 총 측정 실중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(hqTabBranchTotalMeasuredG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">지점 총 환산 중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(hqTabBranchTotalConvertedG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 중량</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(hqTotalMeasuredWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 환산</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(hqTotalConvertedWeightG) }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- ── 지점: 품목별 감정 + 본사 검품 한 표 (본사 검품 화면과 동일 7열) ── -->
      <v-card v-if="showBranchItemCard" rounded="xl" elevation="0" class="info-card mb-4">
        <div class="info-card-header d-flex align-center justify-space-between flex-wrap ga-2 px-5 py-4">
          <div class="d-flex align-center flex-wrap ga-2">
            <div class="header-icon-wrap bg-primary-lighten">
              <v-icon color="primary" size="18">mdi-format-list-bulleted</v-icon>
            </div>
            <span class="text-subtitle-2 font-weight-bold">품목별 감정 · 본사 검품</span>
            <v-chip v-if="hqInspectionDone" size="x-small" color="success" label variant="tonal">
              검품완료
            </v-chip>
            <v-chip v-else size="x-small" color="grey" label variant="tonal">검품 미완료</v-chip>
          </div>
          <v-btn
            v-if="canEditBranchAppraisal"
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="savingItemResults"
            :disabled="loadingAppraisalItems"
            prepend-icon="mdi-check"
            @click="submitAppraisalComplete"
          >
            감정 완료
          </v-btn>
        </div>
        <v-divider />
        <v-tabs v-model="metalTab" color="primary" class="px-2">
          <v-tab value="GOLD" class="font-weight-medium">금 (GOLD)</v-tab>
          <v-tab value="SILVER" class="font-weight-medium">은 (SILVER)</v-tab>
        </v-tabs>
        <v-divider />
        <v-card-text class="pa-5 pt-4">
          <v-alert
            v-if="itemAppraisalReadOnly"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
            icon="mdi-eye-outline"
          >
            교환완료된 예약입니다. 감정 내역은 조회만 가능합니다.
          </v-alert>
          <v-alert
            v-else-if="!hqInspectionDone"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
            icon="mdi-information-outline"
          >
            본사 검품 전까지 검품중량·검품 환산중량은 '-' 로 표시됩니다.
          </v-alert>
          <v-alert
            v-if="appraisalItemsLoadError"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
            rounded="lg"
          >
            {{ appraisalItemsLoadError }}
          </v-alert>
          <v-progress-linear
            v-if="loadingAppraisalItems"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <div class="d-flex flex-wrap ga-4 mb-4 px-1 total-weight-summary">
            <div class="text-body-2">
              <span class="text-medium-emphasis">{{ metalTab === 'GOLD' ? '금' : '은' }} · 지점 총 측정 실중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(totalMeasuredWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">지점 총 환산 중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(totalConvertedWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 중량</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(branchTabHqMeasuredTotalG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 환산</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(branchTabHqConvertedTotalG) }}</span>
            </div>
          </div>

          <div class="hq-inspection-table-wrap">
            <v-table density="compact" class="appraisal-item-table border rounded-lg">
              <thead>
                <tr>
                  <th class="text-left text-no-wrap">현물</th>
                  <th class="text-left">품목</th>
                  <th class="text-end" style="min-width: 100px;">표시비율</th>
                  <th class="text-end text-no-wrap">계산비율</th>
                  <th class="text-end" style="min-width: 132px;">측정 실중량(g)</th>
                  <th class="text-end text-no-wrap">환산 중량(g)</th>
                  <th class="text-end text-no-wrap">검품중량(g)</th>
                  <th class="text-end text-no-wrap">검품 환산중량(g)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in currentAppraisalItems" :key="row.appraisalItemCode">
                  <td class="text-caption text-medium-emphasis text-no-wrap">{{ branchSpotLabelForTab() }}</td>
                  <td class="text-body-2">{{ row.appraisalItemName || '-' }}</td>
                  <td class="text-end py-2">
                    <template v-if="row.standardPercent != null && row.standardPercent !== ''">
                      <span class="text-caption">{{ formatStandardRatioLabel(row.standardPercent) }}</span>
                    </template>
                    <v-text-field
                      v-else
                      :model-value="extraPercentByCode[row.appraisalItemCode]"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      suffix="%"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="예: 30"
                      class="measure-input measure-input--ratio"
                      :disabled="itemAppraisalReadOnly"
                      @update:model-value="v => onExtraPercentInput(row.appraisalItemCode, v)"
                    />
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatStandardRatioLabel(row.conversionPercent) }}
                  </td>
                  <td class="text-end py-2">
                    <v-text-field
                      :model-value="measuredByCode[row.appraisalItemCode]"
                      type="number"
                      step="0.001"
                      min="0"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="measure-input measure-input--hq-weight"
                      :disabled="itemAppraisalReadOnly"
                      @update:model-value="v => onMeasuredInput(row.appraisalItemCode, v)"
                    />
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatWeightG(rowConvertedWeight(row)) }}
                  </td>
                  <td class="text-end text-body-2 text-medium-emphasis">
                    {{ formatWeightG(hqResultRow(row.appraisalItemCode)?.checkWeight) }}
                  </td>
                  <td class="text-end text-body-2 text-primary">
                    {{ formatWeightG(hqResultRow(row.appraisalItemCode)?.conversionWeight) }}
                  </td>
                </tr>
                <tr v-if="!loadingAppraisalItems && currentAppraisalItems.length === 0">
                  <td colspan="8" class="text-center text-medium-emphasis py-8">
                    등록된 품목이 없습니다.
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <div class="d-flex flex-wrap ga-4 mt-4 px-1 total-weight-summary">
            <div class="text-body-2">
              <span class="text-medium-emphasis">{{ metalTab === 'GOLD' ? '금' : '은' }} · 지점 총 측정 실중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(totalMeasuredWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">지점 총 환산 중량</span>
              <span class="font-weight-bold ms-2">{{ formatWeightG(totalConvertedWeightG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 중량</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(branchTabHqMeasuredTotalG) }}</span>
            </div>
            <div class="text-body-2">
              <span class="text-medium-emphasis">검품 총 환산</span>
              <span class="font-weight-bold ms-2 text-primary">{{ formatWeightG(branchTabHqConvertedTotalG) }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

    </template>

    <!-- 본사 검품 완료 안내 -->
    <v-dialog v-model="hqCompleteSuccessDialog" max-width="400">
      <v-card rounded="xl">
        <v-card-text class="pa-8 text-center text-body-1">
          본사 검품이 완료되었습니다.
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="primary" variant="flat" rounded="lg" @click="onCloseHqCompleteDialog">
            확인
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 감정 완료 성공 → 목록 이동 -->
    <v-dialog v-model="completeSuccessDialog" max-width="400" persistent>
      <v-card rounded="xl">
        <v-card-text class="pa-8 text-center text-body-1">
          감정평가가 완료되었습니다.
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="primary" variant="flat" rounded="lg" @click="onCloseAppraisalCompleteDialog">
            목록으로
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 카드 공통 ── */
.info-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.info-card-header {
  background: rgba(0, 0, 0, 0.018);
}

.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
}
.bg-primary-lighten  { background: rgba(var(--v-theme-primary), 0.1); }
.bg-success-lighten  { background: rgba(var(--v-theme-success), 0.1); }

/* ── 예약 정보 그리드 ── */
.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 16px;
}

@media (max-width: 599px) {
  .info-grid { grid-template-columns: 1fr 1fr; }
}

.info-label {
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.info-value {
  color: rgba(0, 0, 0, 0.82);
}

/* ── 감정 내역 테이블 ── */
.lot-table {
  background: transparent !important;
}

.lot-th {
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  color: rgba(0, 0, 0, 0.45) !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 2px solid rgba(0, 0, 0, 0.07) !important;
  white-space: nowrap;
}

.lot-row td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}

.lot-row:last-child td {
  border-bottom: none !important;
}

.lot-row:hover td {
  background: rgba(0, 0, 0, 0.018);
}

.total-weight-summary {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
}

.appraisal-item-table :deep(.measure-input) {
  max-width: 132px;
  margin-left: auto;
}

.appraisal-item-table :deep(.measure-input .v-field__input) {
  min-height: 36px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.hq-inspection-table-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.text-no-wrap {
  white-space: nowrap;
}
</style>
